import React, { useState } from "react";
import { IVehiclePriceGroup } from "../../../reducers/bookings/types";
import MinusIcon from "@material-ui/icons/Remove";
import PlusIcon from "@material-ui/icons/Add";
import { useTranslation } from "react-i18next";
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, makeStyles } from "@material-ui/core";
import CheckRounded from "@material-ui/icons/CheckCircleRounded";
import { Button } from "@material-ui/core";
import { useSnackBar } from "../../SnackBarContext/SnackBarContext";
import { SnackBarVariant } from "../../SnackbarWrapper/SnackbarWrapper";

const useStyles = makeStyles(theme => ({
  container: {
    width: 170,
    height: 38,
    borderRadius: 8,
    background: 'var(--background-secondary)',
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.down('xs')]: {
      width: 130,
      height: 34
    },
    [theme.breakpoints.between(600, 759)]: {
      width: 150,
      height: 36
    }

  },

  overlay: {
    position: 'absolute',
    inset: 0,
    borderRadius: 8,
    backgroundColor: 'var(--primary-color)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    cursor: 'pointer',
  },

  button: {
    width: 36,
    height: 36,
    borderRadius: 8,
    border: '2px solid var(--background-secondary)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',

    [theme.breakpoints.down('xs')]: {
      width: 30,
      height: 30
    },
    [theme.breakpoints.between(600, 1100)]: {
      width: 28,
      height: 28
    }

  },

  minus: {
    backgroundColor: '#fff',
    color: '#e2e2e2'
  },

  minusActive: {
    backgroundColor: '#ffcfcb',
    color: '#e00101'
  },

  plus: {
    backgroundColor: 'var(--primary-color)',
    color: '#fff'
  },

  plusDisabled: {
    backgroundColor: '#e2e2e2',
    color: '#fff',
    cursor: 'not-allowed',
    pointerEvents: 'none'
  },

  text: {
    flex: 1,
    textAlign: 'center',
    fontWeight: 500,
    fontSize: 14,

    [theme.breakpoints.down('xs')]: {
      fontSize: 12
    }
  },

  bold: {
    fontWeight: 600
  },

  select: {
    borderRadius: 8,
    padding: "12px 36px",
    fontWeight: 700,
    fontSize: 16,
    textTransform: "none",
    borderWidth: 2,
    borderColor: "var(--primary-color) !important",
    color: "#fff !important",
    transition: "all 240ms cubic-bezier(0.4, 0, 0.2, 1)",
    backdropFilter: "blur(6px)",
    background: "var(--primary-color) !important",
    "&:hover": {
      background: "var(--primary-color) !important",
      opacity: 0.9,
      filter: "brightness(1.05)",
    },
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1, 1.5),
      fontSize: 14,
    }
  },

  selected: {
    background: "rgba(17, 17, 17, 0.9) !important",
    borderColor: "#000 !important",
    color: "#fff !important",
    "& $checkIcon": { opacity: 1 }
  },

  checkIcon: {
    fontSize: 22,
    opacity: 0,
    transition: "opacity .25s ease",
  },

}));

interface IProps {
  count: number;
  maxCount: number;
  vehicleGroup: IVehiclePriceGroup;
  multipleVehicleBooking: boolean;
  updateCounter: (data: any, isIncrease: boolean, vehicleId?: string, smartCarOnly?: boolean) => void;
  allContactlessGroup?: boolean;
  mixedContactlessGroup?: boolean;
  smartCarOnly?: boolean;
}

const VehicleCounter: React.FC<IProps> = (props) => {

  const classes = useStyles();
  const { count, maxCount, vehicleGroup, multipleVehicleBooking, updateCounter, allContactlessGroup, mixedContactlessGroup, smartCarOnly } = props
  const snackbar = useSnackBar();
  const [open, setOpen] = useState<boolean>(false);
  const { t } = useTranslation();
  const increase = (smartCarOnly?: boolean) => {
    if (count < props.maxCount) {
      updateCounter(vehicleGroup, true, "", smartCarOnly);
    }
  };

  const decrease = (smartCarOnly?: boolean) => {
    if (count > 0) {
      updateCounter(vehicleGroup, false, "", smartCarOnly);
    }
  };



  return (
    <>
      {multipleVehicleBooking ? (
        <div className={classes.container}>
          {false ? (
            <div className={classes.overlay} onClick={() => increase(allContactlessGroup)}>
              {t("misc:select")}
            </div>
          ) : (
            <>
              <div
                className={`${classes.button} ${
                  count > 0 ? classes.minusActive : classes.minus
                }`}
                onClick={() => decrease(allContactlessGroup)}
              >
                <MinusIcon style={{ fontSize: 18 }} />
              </div>

              <div
                className={`${classes.text} ${count > 0 ? classes.bold : ""}`}
              >
                {t("root:selected")}: {count}
              </div>

              <div
                className={`${classes.button} ${
                  count === maxCount ? classes.plusDisabled : classes.plus
                }`}
                onClick={() => {
                  if(mixedContactlessGroup && count == 0){
                    if(smartCarOnly){
                      snackbar({
                        message: t('snackBar:oneVehicleForContactless'),
                        variant: SnackBarVariant.INFORMATION
                      })
                      return;
                    }
                    setOpen(true);
                  }
                  else if(allContactlessGroup){
                    increase(allContactlessGroup)
                  }
                  else {
                    if(smartCarOnly){
                      snackbar({
                        message: t('snackBar:oneVehicleForContactless'),
                        variant: SnackBarVariant.INFORMATION
                      })
                      return;
                    }
                    increase();
                  }
                }}
              >
                <PlusIcon style={{ fontSize: 18 }} />
              </div>
            </>
          )}
        </div>
      ) : (
        <Button
          className={`${classes.select} ${count > 0 ? classes.selected : ""}`}
          onClick={() => {
            if(count == 0 && mixedContactlessGroup){
              setOpen(true);
            }
            else {
              if(count > 0){
                decrease(allContactlessGroup);
              }
              else {
                increase(allContactlessGroup);
              }
            }
          }}
          startIcon={
            count > 0 && <CheckRounded className={classes.checkIcon} />
          }
        >
          {count > 0 ? t("root:booked") : t("misc:buttonTextKey.Book Now")}
        </Button>
      )}
      <Dialog open={open} onClose={() => {setOpen(false)}}>
        <DialogTitle id="alert-dialog-title">{t('root:selectBookingType')}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {t('root:selectBookingTypeDialog')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            increase();
            setOpen(false);
          }} color="primary" variant="outlined">
            {t('root:regularBooking')}
          </Button>
          <Button onClick={() => {
            increase(true)
            setOpen(false);
          }} color="primary" variant="outlined">
            {t('root:contactlessBooking')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default VehicleCounter;
